import { useState } from "react";
import SEOHead from "../components/SEOHead";
import ContactForm from "../components/ContactForm";

export default function Contact() {
  const [openFaq, setOpenFaq] = useState(0);

  const faqs = [
    {
      q: "You want to know if we're a fit before committing.",
      a: "That’s exactly what the discovery call is for. We discuss your business, your challenges, and whether working together makes sense. No pressure."
    },
    {
      q: "You need to know when work can actually begin.",
      a: "Availability varies. Reach out, and I’ll give you a straight answer on timing."
    },
    {
      q: "You're not based in Europe. That's fine.",
      a: "I work with clients globally. Video calls keep things easy, and I adapt to your time zone."
    },
    {
      q: "You'll always know who's doing the work.",
      a: "Everything is delivered by me, directly. No middlemen, no handoffs."
    }
  ];

  return (
    <>
      <SEOHead
        title="Let's Talk | Prajwal Halwai"
        description="Book a discovery call and tell me about your project. I'll listen, ask the right questions, and tell you if I can help."
      />

      {/* Header Banner */}
      <section className="py-16 md:py-20 border-b border-[#181B24]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl space-y-4">
            <span className="text-xs font-semibold uppercase tracking-wider text-[#CC3737]">
              Start a conversation
            </span>
            <h1 className="text-3xl sm:text-5xl font-extrabold text-white tracking-tight">
              Have an idea? Let's talk about it.
            </h1>
            <p className="text-base sm:text-lg text-[#94A3B8] leading-relaxed">
              Not sure where to start? Tell me what’s on your mind, and I’ll tell you if I can help.
            </p>
          </div>
        </div>
      </section>

      {/* Main Content: Form + Contact Info */}
      <section className="py-16 md:py-24 border-b border-[#181B24]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
            
            {/* Form Column */}
            <div className="lg:col-span-7">
              <ContactForm />
            </div>

            {/* Direct Details & Discovery Call Column */}
            <div className="lg:col-span-5 space-y-8">
              {/* Direct Info Card */}
              <div className="rounded-2xl bg-[#11141D] border border-[#202534] p-7 sm:p-8 space-y-6 shadow-xl">
                <div>
                  <span className="text-xs font-semibold uppercase tracking-wider text-[#CC3737]">
                    Contact information
                  </span>
                  <h2 className="text-xl font-bold text-white tracking-tight mt-1">
                    Direct contact details
                  </h2>
                  <p className="text-xs text-[#94A3B8] mt-1">
                    I typically respond within 1-2 business days.
                  </p>
                </div>

                <div className="space-y-4 text-sm">
                  <div className="flex items-start gap-3.5 p-3 rounded-xl bg-[#151924] border border-[#242A39]">
                    <i className="fa-solid fa-user text-[#CC3737] text-base mt-0.5" />
                    <div>
                      <div className="text-xs text-[#64748B]">Consultant</div>
                      <div className="font-semibold text-white">Prajwal Halwai</div>
                    </div>
                  </div>

                  <div className="flex items-start gap-3.5 p-3 rounded-xl bg-[#151924] border border-[#242A39]">
                    <i className="fa-solid fa-location-dot text-[#CC3737] text-base mt-0.5" />
                    <div>
                      <div className="text-xs text-[#64748B]">Office Address</div>
                      <div className="text-white">Østerbro 7, 5000 Odense C, Denmark</div>
                    </div>
                  </div>

                  <div className="flex items-start gap-3.5 p-3 rounded-xl bg-[#151924] border border-[#242A39]">
                    <i className="fa-solid fa-phone text-[#CC3737] text-base mt-0.5" />
                    <div>
                      <div className="text-xs text-[#64748B]">Phone Number (Clickable)</div>
                      <a
                        href="tel:+4550382138"
                        className="text-white hover:text-[#CC3737] transition-colors font-medium whitespace-nowrap block"
                      >
                        +45 50 38 21 38
                      </a>
                    </div>
                  </div>

                  <div className="flex items-start gap-3.5 p-3 rounded-xl bg-[#151924] border border-[#242A39]">
                    <i className="fa-solid fa-envelope text-[#CC3737] text-base mt-0.5" />
                    <div>
                      <div className="text-xs text-[#64748B]">Email Address</div>
                      <a
                        href="mailto:prajwal@prajwalhalwai.com"
                        className="text-white hover:text-[#CC3737] transition-colors font-medium whitespace-nowrap block"
                      >
                        prajwal@prajwalhalwai.com
                      </a>
                    </div>
                  </div>

                  <div className="flex items-start gap-3.5 p-3 rounded-xl bg-[#151924] border border-[#242A39]">
                    <i className="fa-solid fa-building text-[#CC3737] text-base mt-0.5" />
                    <div>
                      <div className="text-xs text-[#64748B]">Business Registration</div>
                      <div className="text-white font-mono text-xs">CVR: 46152085</div>
                    </div>
                  </div>
                </div>

                <div className="pt-2 border-t border-[#1E2330] flex items-center justify-between text-xs text-[#64748B]">
                  <span>Follow on GitHub & LinkedIn:</span>
                  <div className="flex gap-2">
                    <a
                      href="https://linkedin.com/company/prajwal-halwai"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-8 h-8 rounded-lg bg-[#181C26] hover:bg-[#252C3D] flex items-center justify-center text-[#CBD5E1] hover:text-white transition-colors"
                      aria-label="LinkedIn profile"
                    >
                      <i className="fa-brands fa-linkedin-in text-xs" />
                    </a>
                    <a
                      href="https://github.com/prh7"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-8 h-8 rounded-lg bg-[#181C26] hover:bg-[#252C3D] flex items-center justify-center text-[#CBD5E1] hover:text-white transition-colors"
                      aria-label="GitHub profile"
                    >
                      <i className="fa-brands fa-github text-xs" />
                    </a>
                  </div>
                </div>
              </div>

              {/* Discovery Call Promo Box */}
              <div className="p-6 rounded-2xl bg-[#131722] border border-[#242B3C] space-y-3">
                <div className="flex items-center gap-2 text-white font-bold text-base">
                  <i className="fa-solid fa-calendar-clock text-[#CC3737]" />
                  <span>30-minute discovery call</span>
                </div>
                <p className="text-xs sm:text-sm text-[#94A3B8] leading-relaxed">
                  Prefer a direct face-to-face conversation? Submit your details on the form or drop an email to schedule a 30-minute video session.
                </p>
                <div className="pt-1">
                  <a
                    href="mailto:prajwal@prajwalhalwai.com?subject=Discovery%20Call%20Request"
                    className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#CC3737] hover:underline"
                  >
                    <span>Request Discovery Call via Email</span>
                    <i className="fa-solid fa-arrow-right text-[10px]" />
                  </a>
                </div>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* Scraped Frequently Asked Questions */}
      <section className="py-20 bg-[#0C0E14]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14 space-y-3">
            <span className="text-xs font-semibold uppercase tracking-wider text-[#CC3737]">
              FAQ
            </span>
            <h2 className="text-2xl sm:text-4xl font-extrabold text-white tracking-tight">
              Frequently asked questions
            </h2>
            <p className="text-sm text-[#94A3B8]">
              Clear expectations on collaboration, timelines, and communication.
            </p>
          </div>

          <div className="space-y-4">
            {faqs.map((faq, idx) => {
              const isOpen = openFaq === idx;
              return (
                <div
                  key={idx}
                  className="rounded-2xl bg-[#11141C] border border-[#1E2330] overflow-hidden transition-colors"
                >
                  <button
                    type="button"
                    onClick={() => setOpenFaq(isOpen ? null : idx)}
                    className="w-full p-6 text-left flex items-center justify-between gap-4 cursor-pointer hover:bg-[#151922] transition-colors"
                    aria-expanded={isOpen}
                  >
                    <span className="text-base font-bold text-white pr-2">
                      {faq.q}
                    </span>
                    <span
                      className={`flex-shrink-0 w-8 h-8 rounded-full bg-[#181C26] border border-[#252C3D] flex items-center justify-center text-xs text-[#CBD5E1] transition-transform duration-300 ease-in-out ${
                        isOpen ? "rotate-180 text-[#CC3737]" : ""
                      }`}
                    >
                      <i className="fa-solid fa-chevron-down" />
                    </span>
                  </button>

                  <div
                    className={`grid transition-[grid-template-rows] duration-300 ease-in-out ${
                      isOpen ? "grid-rows-[1fr]" : "grid-rows-[0fr]"
                    }`}
                  >
                    <div className="overflow-hidden">
                      <div className="px-6 pb-6 pt-1 text-sm text-[#94A3B8] leading-relaxed border-t border-[#181C26]">
                        {faq.a}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>
    </>
  );
}
